import { type ForwardedRef, forwardRef, type ReactNode } from "react";

import { cn } from "$/lib/utils/functions/misc.functions";

import type { DropdownOptionListProps } from "./Dropdown.types";
import DropdownOption from "./DropdownOption";

function DropdownOptionsListInner<T>(
  {
    id,
    options,
    focusedOptionIndex,
    multi,
    selected,
    handleSetFocusedOptionIndex,
    handleSelect,
  }: DropdownOptionListProps<T>,
  listRef: ForwardedRef<HTMLUListElement>,
) {
  if (options.length === 0) {
    return (
      <div className="flex h-56 flex-col items-center justify-center gap-2 pb-6">
        <p className="text-grey-400 text-center text-sm">
          Aucun résultat trouvé
        </p>
      </div>
    );
  }

  return (
    <ul
      id={id}
      ref={listRef}
      role="listbox"
      aria-multiselectable={multi}
      aria-activedescendant={
        focusedOptionIndex !== -1 ? `option-${focusedOptionIndex}` : undefined
      }
      className="scrollbar-custom z-50 mx-auto max-h-56 w-11/12 space-y-1 overflow-auto overscroll-contain bg-white pb-1"
    >
      {options.map((option, i) => {
        let isSelected = false;
        if (selected) {
          if (multi) {
            if (!Array.isArray(selected)) {
              throw new Error("Multi select options must be an array");
            }
            isSelected = selected.includes(option.value);
          } else {
            isSelected = selected === option.value;
          }
        }

        return (
          <li
            key={i}
            role="option"
            data-focused={focusedOptionIndex === i}
            aria-selected={isSelected}
            title={option.label}
            onMouseOver={() => handleSetFocusedOptionIndex(i)}
            tabIndex={-1}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelect(option.value, i);
            }}
            className={cn(
              "relative rounded-none border-b px-2 py-1 text-start font-medium text-black transition-colors *:cursor-pointer last:border-transparent hover:bg-primary-200 data-[focused=true]:bg-primary-200",
              {
                "bg-primary-200": isSelected,
              },
            )}
          >
            {option.render?.({
              value: option.value,
              label: option.label,
              isSelected,
              multi,
            }) ?? (
              <DropdownOption
                label={option.label}
                isSelected={isSelected}
                multi={multi}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

const DropdownOptionsList = forwardRef(DropdownOptionsListInner) as {
  <T>(
    props: DropdownOptionListProps<T> & {
      ref?: ForwardedRef<HTMLUListElement>;
    },
  ): ReactNode;
  displayName: string;
};
DropdownOptionsList.displayName = "DropdownOptionsList";

export default DropdownOptionsList;
